import {
  GenericPipelineTemplateFragment,
  LoadGenericPipelineTemplatesDocument,
  LoadGenericPipelineTemplatesQuery,
  LoadGenericPipelineTemplatesQueryVariables,
  ProcessingPipelineRowFragment,
  ProcessingPipelinesConnectionFragment,
  SortDirection,
} from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import { atom, selector } from 'recoil';
import { persistAtom } from '../../common/recoil/persistAtom';
import { AsyncLoadStatus, DataTableState } from '../../common/types';
import { secureClient } from '../../GraphQLClient';
import {
  ProcessingPipelinesTableColumn,
  ProcessingPipelineTabId,
} from './processingPipelineJobs.types';

const getKey = (postfix: string) => `warebee-processing-pipelines-${postfix}`;

export const processingPipelinesData =
  atom<ProcessingPipelinesConnectionFragment>({
    key: getKey('data'),
    default: null,
  });

export const processingPipelineSelectedId = atom<string>({
  key: getKey('selected-id'),
  default: null,
});

export const processingPipelineSelected =
  selector<ProcessingPipelineRowFragment>({
    key: getKey('selected'),
    get: ({ get }) => {
      const selectedId = get(processingPipelineSelectedId);
      const pipelines = get(processingPipelinesData)?.content;
      return _.find(pipelines, p => p.id === selectedId);
    },
  });

export const processingPipelinesDataStatus = atom<AsyncLoadStatus>({
  key: getKey('data-status'),
  default: AsyncLoadStatus.None,
});

export const processingPipelinesDataTableState = atom<
  DataTableState<ProcessingPipelinesTableColumn>
>({
  key: getKey('data-table-state'),
  default: {
    searchValues: {},
    sortValues: {
      createdAt: SortDirection.ASC,
    },
  },
});

export const processingPipelineSelectedTriggerId = atom<string>({
  key: getKey('selected-trigger-id'),
  default: null,
});

export const processingPipelineUpdateStatus = atom<AsyncLoadStatus>({
  key: getKey('update-status'),
  default: AsyncLoadStatus.None,
});

export const processingPipelineIsActiveEditor = atom<boolean>({
  key: getKey('is-active-editor'),
  default: false,
});

const processingPipelineSelectedTabIdAtom =
  persistAtom<ProcessingPipelineTabId>({
    key: getKey('pipeline-settings-tab-key-atom'),
    default: 'tab-pipeline-settings-properties',
  });

export const processingPipelineSelectedTabId =
  selector<ProcessingPipelineTabId>({
    key: getKey('settings-tag-id'),
    get: ({ get }) => get(processingPipelineSelectedTabIdAtom),
    set: ({ set }, value: ProcessingPipelineTabId) => {
      set(processingPipelineSelectedTabIdAtom, value);
    },
  });

export const processingPipelineGenericTemplates = selector<
  Record<string, GenericPipelineTemplateFragment>
>({
  key: getKey('generic-templates'),
  get: async () => {
    try {
      const response = await secureClient.query<
        LoadGenericPipelineTemplatesQuery,
        LoadGenericPipelineTemplatesQueryVariables
      >({
        query: LoadGenericPipelineTemplatesDocument,
      });
      if (!_.isEmpty(response.errors)) {
        console.error(
          'Cannot Processing Pipeline Generic Templates',
          response.errors,
        );
        throw new Error("Cannot load plane's bays");
      }
      const templates = response.data.genericPipelineTemplates;
      return _.keyBy(templates, 'id');
    } catch (ex) {
      console.error('Cannot Load Processing Pipeline Generic Templates', ex);
      throw new Error('Cannot Load Processing Pipeline Generic Templates');
    }
  },
});
